import { useGetForfait } from "@/api/forfait";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useAuth } from "@/context/auth/AuthContext";
import {
  isSupportedLocale,
  setMainLanguage,
  supportedLocales,
  SupportedLocale,
} from "@/i18n";
import { Forfait } from "@/lib/types";
import { CheckCircleIcon, Crown, UserCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowOptionsDialog from "@/components/hd/ShowOptionsDialog";

const ComptePrincipal = () => {
  const { t, i18n } = useTranslation();
  const { instanceData } = useAuth();

  const { data: forfait } = useGetForfait();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [selectedOptionName, setSelectedOptionName] = useState<string>("");
  const [totalWeight, setTotalWeight] = useState<number>(0);

  function subscribeToOptionSuccess(name: string) {
    setSelectedOptionName(name);
    setOpenConfirmation(true);
  }

  useEffect(() => {
    let weight = forfait?.maxWeight ?? 0
    if (forfait?.subscribedOptions?.length ?? 0 > 0) {
      const option = forfait?.subscribedOptions.find((option) => option.type === 'HD');
      if (option) {
        weight += option.maxWeight ?? 0;
      }
    }
    setTotalWeight(weight);
  }, [forfait]);

  const createdAt = useMemo(() => {
    if (forfait?.subscriptionStart) {
      return new Date(forfait.subscriptionStart).toLocaleDateString(
        i18n.language,
        {
          year: "numeric",
          month: "long",
          day: "numeric",
        },
      );
    }
    return "No date";
  }, [forfait, i18n.language]);

  const currentUiLocale = useMemo((): SupportedLocale => {
    const lng = i18n.language ?? "fr";
    if (isSupportedLocale(lng)) return lng;
    const short = lng.slice(0, 2);
    return isSupportedLocale(short) ? short : "fr";
  }, [i18n.language]);

  const getArtworksSpace = () => {
    if (forfait?.numberArtworks && forfait?.maxArtworks) {
      return (forfait?.numberArtworks / forfait?.maxArtworks) * 100 > 100 ? 100 : (forfait?.numberArtworks / forfait?.maxArtworks) * 100;
    }
    
    return 0;
  };

  const getSpaceSpace = () => {
    if (forfait?.usedSpaceInGB && totalWeight) {
      return (forfait?.usedSpaceInGB / totalWeight) * 100 > 100 ? 100 : (forfait?.usedSpaceInGB / totalWeight) * 100;
    }
    return 0;
  };

  //   const CurrentLanguageFlag = languageToFlag[i18n.language as SupportedLocale];

  return (
    <div className="px-[80px] py-[48px]">
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-row items-center gap-2">
          <p className="text-gray-900 text-[32px] font-semibold font-[Instrument Sans] leading-normal">
            {t("mainAccount")}
          </p>
        </div>
      </div>
      <div className="flex flex-row gap-4  max-w-screen-2xl mt-8">
        <div className="flex flex-col gap-4 w-1/3">
          <div className="flex flex-col h-fit items-center gap-2 bg-white rounded-md border border-gray-200 p-8">
          <div className="flex flex-col items-center gap-2 mb-8">
            <UserCircle className="w-[66px] h-[66px] text-[#008BD4]" />
            <p className="text-gray-900 text-[18px] font-semibold font-[Raleway] leading-normal">
              {forfait?.clientName}
            </p>
          </div>
          <div className="w-full">
            <div className="flex flex-row justify-between items-center">
              <p className="text-gray-500 text-[14px] font-[500] font-[Raleway]">
                {t("creationDate")}
              </p>
              <p className="text-gray-900 text-[16px] font-[500] font-[Raleway]">
                {createdAt}
              </p>
            </div>
          </div>
          {/* <div className="w-full">
            <div className="flex flex-row justify-between items-start">
              <p className="text-gray-500 text-[14px] font-[500] font-[Raleway]">
                {t("interfaceLanguage")}
              </p>
              <div>
                <div className="flex flex-row items-center gap-2 justify-end">
                  <CurrentLanguageFlag height={16} width={16} />
                  <p className="text-gray-900 text-[16px] font-[500] font-[Raleway] text-right">
                    {t(`language_${i18n.language}`)}
                  </p>
                </div>
                <UpdateLangModal />
              </div>
            </div>
          </div> */}
          </div>
          {instanceData?.authorizeBoLangChange === true && (
            <div className="bg-white rounded-md border border-gray-200 p-4">
              <p className="text-gray-500 text-[14px] font-[500] font-[Raleway] mb-2">
                {t("interfaceLanguage")}
              </p>
              <Select
                value={currentUiLocale}
                onValueChange={(value) =>
                  setMainLanguage(value as SupportedLocale)
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {supportedLocales
                    .filter((locale) => locale !== "nl")
                    .map((locale) => (
                      <SelectItem key={locale} value={locale}>
                        {t(`language_${locale}`)}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="flex flex-col h-fit items-center gap-6 w-2/3 bg-white rounded-md border border-gray-200">
          <div className="flex flex-col p-8 w-full gap-6">
            <div className="flex items-center gap-2 w-full">
              <p className="text-gray-900 text-[18px] font-semibold font-[Raleway] leading-normal">
                {t("mainSubSubscription")}
              </p>
              <Badge variant="outlinedActive">{forfait?.name}</Badge>
            </div>
            <div className="flex flex-col gap-2 w-full">
              <div className="flex flex-row gap-3 items-center">
                <p className="text-gray-900 text-[16px] font-[500] font-[Raleway]">
                  {t("mainSubStockageWeigth")}
                </p>
                <p className="text-gray-600 text-[16px] font-[500] font-[Raleway]">
                  {" "}
                  {forfait?.usedSpaceInGB} / {totalWeight} Go
                </p>
              </div>
              <Progress value={getSpaceSpace()} indicatorClassName={`${getSpaceSpace() < 70 ? 'bg-primary' : getSpaceSpace() < 90 ? 'bg-amber-500' : 'bg-red-500'}`} className="w-full" />
            </div>
            <div className="flex flex-col gap-2 w-full">
              <div className="flex flex-row gap-3 items-center">
                <p className="text-gray-900 text-[16px] font-[500] font-[Raleway]">
                  {t("mainSubArtworks")}
                </p>
                <p className="text-gray-600 text-[16px] font-[500] font-[Raleway]">
                  {" "}
                  {forfait?.numberArtworks} / {forfait?.maxArtworks}
                </p>
              </div>
              <Progress value={getArtworksSpace()} className="w-full" color={getArtworksSpace() < 70 ? 'primary' : getArtworksSpace() < 90 ? 'amber-500' : 'red-500'} />
            </div>
            <div className="flex flex-col gap-2 w-full">
              <div className="flex flex-row gap-3 mb-1 items-center">
                <p className="text-gray-900 text-[16px] font-[500] font-[Raleway]">
                  {t("mainSubUsers")}
                </p>
              </div>
              {forfait?.users.map((user) => (
                <div className="flex flex-col gap-2 items-center">
                  <p className="text-gray-700 text-[14px] font-[500] font-[Raleway] text-left w-full">
                    {user.user.name} {user.user.lastName}
                  </p>
                  <Separator
                    orientation="vertical"
                    className="w-full h-[0.5px] bg-gray-300"
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="px-4 w-full">
            <Separator
              orientation="vertical"
              className="w-full h-[0.5px] bg-gray-300"
            />
            <div className="flex flex-row w-full justify-end p-6">
              <Button onClick={() => setShowDialog(true)}>
                <Crown className="w-4 h-4 mr-4" />
                {t("manageOptions")}
              </Button>
            </div>
          </div>
          
        </div>
      </div>
      <ShowOptionsDialog open={showDialog} onOpenChange={(open) => setShowDialog(open)} forfait={forfait ?? {} as Forfait} setSelectedOptionName={subscribeToOptionSuccess} />
      <Dialog open={openConfirmation} onOpenChange={setOpenConfirmation}>
        <DialogContent className="p-6">
          <div className="flex items-center justify-center my-4">
            <CheckCircleIcon className="w-8 h-8 text-primary" />
          </div>
          <div className="text-center text-2xl mx-4">
            {t("optionAjoutSuccesTitre1")} <strong>"{selectedOptionName}"</strong> {t("optionAjoutSuccesTitre2")}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComptePrincipal;
